import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import List from '../List/List';
import AutocompleteItem from '../Autocomplete/AutocompleteItem';
import styles from './AutocompleteV2.module.scss';
import TextField from '../Base/TextField/TextField';
import apiClient from '../../Services/NetteApiClient';
import * as ReactDOM from 'react-dom';
import Text from '../Base/Text/Text';

const TextFieldWithRef = React.forwardRef((props, ref) => (
	<TextField {...props} forwardRef={ref} />
));

interface AutocompleteV2Props {
	items?: any[];
	onChange: (selected: any) => void;
}

export default function AutocompleteV2(props: AutocompleteV2Props) {
	const [items, setItems] = useState(props.items || null);
	const [selected, setSelected] = useState([]);
	const elRef = useRef(null);
	const boxRef = useRef(null);
	const inputRef = useRef(null);

	const sections = props.sections || [{ type: 'default' }];

	useEffect(() => {
		setItems(props.items);
	}, [props.items]);

	useEffect(() => {
		props.onChange && props.onChange(selected);
	}, [selected]);

	useEffect(() => {
		document.addEventListener('click', handleClickOutside, true);

		if (inputRef.current) {
			inputRef.current.addEventListener('focus', handleInputFocus, true);
		}

		boxRef.current.addEventListener('click', handleElFocus, true);

		return () => {
			document.removeEventListener('click', handleClickOutside, true);
			if (inputRef.current) {
				inputRef.current.removeEventListener('focus', handleInputFocus, true);
			}
			boxRef.current.removeEventListener('click', handleElFocus, true);
		};
	}, []);

	const handleInputFocus = () => {
		setItems(null);
	};

	const handleElFocus = () => {
		inputRef.current.focus();
	};

	const handleClickOutside = event => {
		const domNode = ReactDOM.findDOMNode(elRef.current);

		if (!domNode || !domNode.contains(event.target)) {
			setItems(null);
		}
	};

	const handleSearch = value => {
		props.onSearch && props.onSearch(value);
	};

	const handleItemClick = item => {
		setSelected(prev => {
			const found = prev.find(p => p.id === item.id);

			if (found) {
				prev = prev.filter(p => p.id !== item.id);
			} else {
				prev.push(item);
			}

			return [...prev];
		});

		inputRef.current.value = '';
	};

	return (
		<div className={styles.container} ref={elRef}>
			<div className={[styles.box, props.className].join(' ')} ref={boxRef}>
				{selected.map((item, index) => {
					return (
						<span className={styles.tag} key={index}>
							{item.label}{' '}
							<img
								src={'/dist/icons/xmark-solid.svg'}
								width={'6px'}
								onClick={() => handleItemClick(item)}
							/>
						</span>
					);
				})}

				<TextFieldWithRef
					ref={inputRef}
					onChange={handleSearch}
					placeholder={props.inputPlaceholder}
					className={styles.input}
					bounce={350}
				/>
			</div>
			{items && items.length > 0 && (
				<div className={styles.list}>
					{sections.map((section, index) => {
						const sectionItems = items.filter(
							i =>
								(!i.type && section.type === 'default') ||
								(i.type && i.type === section.type),
						);

						return (
							<>
								{sectionItems.length > 0 && (
									<div className={styles.section}>
										{section.label && (
											<Text tx={section.label} className={styles.sectionName} />
										)}
										<List
											items={sectionItems}
											itemTemplate={({ item }) => (
												<AutocompleteItem
													item={item}
													itemTemplate={props.itemTemplate}
													onClick={handleItemClick}
													selected={selected.find(s => s.id === item.id)}
												/>
											)}
										/>
										{section.showMore && (
											<div className={styles.sectionFooter}>
												{props.selectedSection ? (
													<div onClick={() => props.onSectionSelect(null)}>
														<Text
															tx={'Zobrazit méně'}
															className={styles.showMoreLess}
														/>
													</div>
												) : (
													<div onClick={() => props.onSectionSelect(section)}>
														<Text
															tx={'Zobrazit více'}
															className={styles.showMoreLess}
														/>
													</div>
												)}
											</div>
										)}
									</div>
								)}
							</>
						);
					})}
				</div>
			)}
		</div>
	);
}

AutocompleteV2.propTypes = {
	itemTemplate: PropTypes.any.required,
};
